/**
 * Admin API service — SMTP configuration, import management, and notification settings
 */
import api, { getErrorMessage, postMultipart } from './api'
import type { SmtpSettings, ImportLog, NotificationSettings, EmailTemplate, EmailTemplateCreate, EmailTemplateUpdate, EmailTemplatePreviewResponse, SystemMessage, CacheStatus, CacheJobStatus, AuditLogPage } from '../types'

export async function getSmtpSettings(): Promise<SmtpSettings> {
  try {
    const response = await api.get<SmtpSettings>('/v1/admin/smtp')
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function updateSmtpSettings(data: Partial<SmtpSettings>): Promise<SmtpSettings> {
  try {
    const response = await api.put<SmtpSettings>('/v1/admin/smtp', data)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function testSmtpSettings(
  recipientEmail?: string,
): Promise<{ success: boolean; message: string }> {
  try {
    const response = await api.post<{ success: boolean; message: string }>(
      '/v1/admin/smtp/test',
      recipientEmail ? { recipient_email: recipientEmail } : {},
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

// ===== Import Management =====

export async function triggerImport(
  importType: 'full' | 'daily',
  options: { serviceCode?: string; useCachedOnly?: boolean; forceDownload?: boolean } = {}
): Promise<{ message: string; import_type: string; service_code: string | null }> {
  try {
    const params: Record<string, string | boolean> = { import_type: importType }
    if (options.serviceCode) params.service_code = options.serviceCode
    if (options.forceDownload) params.force_download = true
    if (options.useCachedOnly) params.use_cached_only = true
    const response = await api.post('/v1/admin/import/trigger', null, { params })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function getImportStatus(limit = 20): Promise<ImportLog[]> {
  try {
    const response = await api.get<ImportLog[]>('/v1/admin/import/status', {
      params: { limit },
    })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function getLatestImport(): Promise<ImportLog | null> {
  try {
    const response = await api.get<ImportLog | null>('/v1/admin/import/latest')
    return response.data
  } catch {
    // Non-fatal for dashboard widget
    return null
  }
}

export async function getRunningImport(): Promise<ImportLog | null> {
  try {
    const response = await api.get<ImportLog | null>('/v1/admin/import/running')
    return response.data
  } catch {
    // Non-fatal for dashboard widget
    return null
  }
}

// ===== Sync (fcc_sync.py — idempotent download + import) =====

export async function triggerSync(
  mode: 'daily' | 'weekly' | 'update',
  options: { serviceCode?: string; forceDownload?: boolean } = {}
): Promise<{ message: string; mode: string; service_code: string | null; triggered?: boolean; status?: string }> {
  try {
    const params: Record<string, string | boolean> = { mode }
    if (options.serviceCode) params.service_code = options.serviceCode
    if (options.forceDownload) params.force_download = true
    const response = await api.post('/v1/admin/sync/trigger', null, { params })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

// ===== Cache Management =====

export async function getCacheStatus(): Promise<CacheStatus> {
  try {
    const response = await api.get<CacheStatus>('/v1/admin/cache')
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function deleteCacheBundle(bundleKey: string): Promise<void> {
  try {
    await api.delete(`/v1/admin/cache/${encodeURIComponent(bundleKey)}`)
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * Start a background download of an alternate URL into a bundle's cache slot (no
 * import). For when FCC's canonical download is unavailable but the file is
 * reachable at a different FCC path. The server requires an http(s) fcc.gov URL.
 *
 * Returns as soon as the job is queued — poll getCacheBundleJob() for the outcome.
 */
export async function loadCacheBundleFromUrl(bundleKey: string, url: string): Promise<CacheJobStatus> {
  try {
    const response = await api.post<CacheJobStatus>(
      `/v1/admin/cache/${encodeURIComponent(bundleKey)}/load-from-url`,
      { url },
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * Start a background freshness check of one bundle against FCC's canonical URL.
 * Unchanged → the cache expiry is extended; newer (or nothing cached) → it is
 * downloaded. Poll getCacheBundleJob() for the outcome.
 */
export async function refreshCacheBundle(bundleKey: string): Promise<CacheJobStatus> {
  try {
    const response = await api.post<CacheJobStatus>(
      `/v1/admin/cache/${encodeURIComponent(bundleKey)}/refresh`,
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/** The most recent cache job for a bundle (running or finished), or null if none. */
export async function getCacheBundleJob(bundleKey: string): Promise<CacheJobStatus | null> {
  try {
    const response = await api.get<CacheJobStatus | null>(
      `/v1/admin/cache/${encodeURIComponent(bundleKey)}/job`,
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

// ===== Import Settings =====

export async function getImportSettings(): Promise<import('../types').ImportSettings> {
  try {
    const response = await api.get('/v1/admin/import-settings')
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function updateImportSettings(states: string[], services: string[]): Promise<void> {
  try {
    await api.put('/v1/admin/import-settings', { monitored_states: states, monitored_services: services })
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

// ===== Notification Settings =====

export async function getNotificationSettings(): Promise<NotificationSettings> {
  try {
    const response = await api.get<NotificationSettings>('/v1/admin/notifications')
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function updateNotificationSettings(data: Partial<NotificationSettings>): Promise<NotificationSettings> {
  try {
    const response = await api.put<NotificationSettings>('/v1/admin/notifications', data)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

// ===== Company Logo =====

export async function uploadCompanyLogo(file: File): Promise<{ bytes: number; content_type: string }> {
  try {
    const form = new FormData()
    form.append('file', file)
    const response = await postMultipart<{ bytes: number; content_type: string }>(
      '/v1/branding/logo',
      form
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function deleteCompanyLogo(): Promise<void> {
  try {
    await api.delete('/v1/branding/logo')
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

// ===== Import Abort =====

export async function abortRunningImport(): Promise<import('../types').ImportLog> {
  try {
    const response = await api.post<import('../types').ImportLog>('/v1/admin/import/abort-running', {})
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

// ===== Email Templates =====

export async function listEmailTemplates(): Promise<EmailTemplate[]> {
  try {
    const response = await api.get<EmailTemplate[]>('/v1/admin/email-templates')
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function listSystemMessages(): Promise<SystemMessage[]> {
  try {
    const response = await api.get<SystemMessage[]>('/v1/admin/email-templates/system-messages')
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function createEmailTemplate(data: EmailTemplateCreate): Promise<EmailTemplate> {
  try {
    const response = await api.post<EmailTemplate>('/v1/admin/email-templates', data)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function assignEmailTemplateRole(
  key: string,
  role: string | null,
): Promise<EmailTemplate> {
  try {
    const response = await api.put<EmailTemplate>(
      `/v1/admin/email-templates/${key}/role`,
      { role },
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function deleteEmailTemplate(key: string): Promise<void> {
  try {
    await api.delete(`/v1/admin/email-templates/${key}`)
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function updateEmailTemplate(key: string, data: EmailTemplateUpdate): Promise<EmailTemplate> {
  try {
    const response = await api.patch<EmailTemplate>(`/v1/admin/email-templates/${key}`, data)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function resetEmailTemplate(key: string): Promise<EmailTemplate> {
  try {
    const response = await api.post<EmailTemplate>(`/v1/admin/email-templates/${key}/reset`, {})
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function previewEmailTemplate(
  key: string,
  overrides?: { subject_template?: string; body_markdown?: string },
): Promise<EmailTemplatePreviewResponse> {
  try {
    const response = await api.post<EmailTemplatePreviewResponse>(
      `/v1/admin/email-templates/${key}/preview`,
      overrides ?? {},
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function testSendEmailTemplate(
  key: string,
  payload?: { recipient_email?: string; subject_template?: string; body_markdown?: string },
): Promise<{ detail: string; recipient: string }> {
  try {
    const response = await api.post<{ detail: string; recipient: string }>(
      `/v1/admin/email-templates/${key}/test-send`,
      payload ?? {},
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/** Columns the audit-log endpoint will order by — mirrors `_AUDIT_SORTS` in admin.py. */
export type AuditSortKey = 'timestamp' | 'event_type' | 'user_email' | 'success'

export type AuditReviewFilter = 'any' | 'unreviewed' | 'reviewed'

/** Mirrors `audit_categories.KEYS` on the server; it refuses anything else. */
export const AUDIT_CATEGORIES = [
  { value: 'security', label: 'Sign-in & security' },
  { value: 'crm', label: 'Clients, leads & messages' },
  { value: 'licenses', label: 'Licenses & construction' },
  { value: 'admin', label: 'Administration, files & exports' },
  { value: 'other', label: 'Other' },
] as const
export type AuditCategory = typeof AUDIT_CATEGORIES[number]['value']

export interface AuditLogFilters {
  page?: number
  page_size?: number
  user_email?: string
  event_type?: string
  ip_address?: string
  resource?: string
  success?: boolean | null
  date_from?: string
  date_to?: string
  /** Only the events flagged for review (what the Audit Log badge counts). */
  significant_only?: boolean
  /** Categories to show; omitted or all of them means everything. */
  categories?: readonly AuditCategory[]
  /** For flagged entries: only those nobody has reviewed, only reviewed ones, or both. */
  review?: AuditReviewFilter
  sort?: AuditSortKey
  order?: 'asc' | 'desc'
}

export async function listAuditLog(filters: AuditLogFilters = {}): Promise<AuditLogPage> {
  const params: Record<string, string | number | boolean | readonly string[]> = {}
  if (filters.page) params.page = filters.page
  if (filters.page_size) params.page_size = filters.page_size
  if (filters.user_email) params.user_email = filters.user_email
  if (filters.event_type) params.event_type = filters.event_type
  if (filters.ip_address) params.ip_address = filters.ip_address
  if (filters.resource) params.resource = filters.resource
  if (filters.success !== undefined && filters.success !== null) params.success = filters.success
  if (filters.date_from) params.date_from = filters.date_from
  if (filters.date_to) params.date_to = filters.date_to
  if (filters.significant_only) params.significant_only = true
  // Every category ticked is the same as no filter, and cheaper for the server.
  if (filters.categories && filters.categories.length < AUDIT_CATEGORIES.length) {
    params.category = filters.categories
  }
  if (filters.review && filters.review !== 'any') params.review = filters.review
  // Sent only when set, so an omitted sort gets the server's default (newest first)
  // rather than this file's idea of it — one default, on the server.
  if (filters.sort) params.sort = filters.sort
  if (filters.order) params.order = filters.order
  try {
    // `category=a&category=b`, the form FastAPI reads as a list (not `category[]=`).
    const response = await api.get<AuditLogPage>('/v1/admin/audit', {
      params, paramsSerializer: { indexes: null },
    })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function getAuditSignificantCount(): Promise<number> {
  try {
    const response = await api.get<{ count: number }>('/v1/admin/audit/significant-count')
    return response.data.count
  } catch {
    return 0
  }
}

export interface AuditEventType {
  event_type: string
  label: string
  category: AuditCategory
  count: number
}

/** The event types present in the log, for the Event select. */
export async function getAuditEventTypes(): Promise<AuditEventType[]> {
  try {
    const response = await api.get<AuditEventType[]>('/v1/admin/audit/event-types')
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export interface AuditSuggestion {
  value: string
  count: number
  last_seen: string
}

/** Values for the IP address / user boxes matching what has been typed, newest first. */
export async function suggestAuditValues(
  field: 'ip_address' | 'user_email', q: string,
): Promise<AuditSuggestion[]> {
  try {
    const response = await api.get<AuditSuggestion[]>('/v1/admin/audit/suggest', {
      params: { field, q },
    })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

// ===== Security alerts (app/services/security_alerts.py) =====

export type SecurityAlertSeverity = 'critical' | 'high' | 'medium'
export type SecurityAlertStatus = 'open' | 'acknowledged' | 'all'

export interface SecurityAlert {
  id: number
  created_at: string
  last_seen_at: string
  occurrences: number
  rule: string
  severity: SecurityAlertSeverity
  title: string
  summary: string
  subject_email: string | null
  ip_address: string | null
  audit_log_id: number | null
  emailed_at: string | null
  acknowledged_at: string | null
  acknowledged_by_name: string | null
  acknowledgement_note: string | null
}

export interface SecurityAlertPage {
  items: SecurityAlert[]
  total: number
  page: number
  page_size: number
}

export async function listSecurityAlerts(params: {
  status?: SecurityAlertStatus
  severity?: SecurityAlertSeverity
  page?: number
  page_size?: number
}): Promise<SecurityAlertPage> {
  try {
    const response = await api.get<SecurityAlertPage>('/v1/admin/security-alerts', { params })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/** Open alerts: `urgent` (critical + high) drives the banner and the menu badge. */
export async function getSecurityAlertCounts(): Promise<{ urgent: number; other: number }> {
  const response = await api.get<{ urgent: number; other: number }>('/v1/admin/security-alerts/counts')
  return response.data
}

export async function acknowledgeSecurityAlert(id: number, note?: string): Promise<SecurityAlert> {
  try {
    const response = await api.post<SecurityAlert>(
      `/v1/admin/security-alerts/${id}/acknowledge`, { note: note || null },
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * Mark flagged audit entries reviewed: the ticked ids, or every entry matching the
 * filters on screen. The server marks only flagged, not-yet-reviewed entries.
 */
export async function reviewAuditEntries(
  target: { ids: number[] } | { filters: AuditLogFilters },
  note?: string,
): Promise<number> {
  const body: Record<string, unknown> = { note: note || null }
  if ('ids' in target) {
    body.ids = target.ids
  } else {
    const f = target.filters
    body.filters = {
      user_email: f.user_email || null,
      event_type: f.event_type || null,
      ip_address: f.ip_address || null,
      resource: f.resource || null,
      success: f.success ?? null,
      date_from: f.date_from || null,
      date_to: f.date_to || null,
      significant_only: true,
      category: f.categories && f.categories.length < AUDIT_CATEGORIES.length ? f.categories : null,
      review: 'unreviewed',
    }
  }
  try {
    const response = await api.post<{ reviewed: number }>('/v1/admin/audit/review', body)
    return response.data.reviewed
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}
